import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="jobs-filters"
export default class extends Controller {
  static targets = ['search', 'select', 'loading', 'content', 'jobsContent', 'form'];

  connect() {
    // Load jobs asynchronously on page load (ensure DOM is ready)
    requestAnimationFrame(() => {
      this.loadJobs();
    });

    // Intercept form submission to load via AJAX
    if (this.hasFormTarget) {
      this.formTarget.addEventListener('submit', (e) => {
        e.preventDefault();
        this.loadJobs();
      });
    }

    // Auto-submit on filter change
    if (this.hasFormTarget) {
      this.selectTargets.forEach((select) => {
        select.addEventListener('change', () => {
          this.loadJobs();
        });
      });

      // Debounced search
      let searchTimeout;
      if (this.hasSearchTarget) {
        this.searchTarget.addEventListener('input', () => {
          clearTimeout(searchTimeout);
          searchTimeout = setTimeout(() => {
            this.loadJobs();
          }, 500);
        });
      }
    }
  }

  loadJobs() {
    // Try to find targets, with fallback to querySelector
    const loadingEl = this.hasLoadingTarget
      ? this.loadingTarget
      : this.element.querySelector('[data-jobs-filters-target="loading"]');
    const jobsContentEl = this.hasJobsContentTarget
      ? this.jobsContentTarget
      : this.element.querySelector('[data-jobs-filters-target="jobsContent"]');
    const formEl = this.hasFormTarget
      ? this.formTarget
      : this.element.querySelector('form.jobs__filters');

    if (!loadingEl || !jobsContentEl || !formEl) {
      return;
    }

    // Show loading state
    this.showLoading(loadingEl, jobsContentEl);

    // Build URL with current form params
    const formData = new FormData(formEl);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (value) params.append(key, value);
    });

    const url = `${formEl.action}?${params.toString()}`;

    // Fetch jobs
    fetch(url, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        this.renderJobs(data.jobs_html, jobsContentEl);
        this.updateFilterOptions(data.filter_options);
        this.hideLoading(loadingEl, jobsContentEl);
      })
      .catch((error) => {
        this.showError(jobsContentEl);
        this.hideLoading(loadingEl, jobsContentEl);
      });
  }

  showLoading(loadingEl, jobsContentEl) {
    if (loadingEl) loadingEl.hidden = false;
    if (jobsContentEl) jobsContentEl.hidden = true;
  }

  hideLoading(loadingEl, jobsContentEl) {
    if (loadingEl) {
      loadingEl.hidden = true;
      loadingEl.style.display = 'none'; // Fallback
    }
    if (jobsContentEl) {
      jobsContentEl.hidden = false;
      jobsContentEl.style.display = ''; // Fallback
    }
  }

  showError(jobsContentEl) {
    if (jobsContentEl) {
      jobsContentEl.innerHTML = `
        <div class="container">
          <p class="jobs__empty">${this.t(
            'website.jobs.error',
            'Error loading jobs. Please try again.'
          )}</p>
        </div>
      `;
      jobsContentEl.hidden = false;
    }
  }

  renderJobs(jobsHtml, jobsContentEl) {
    if (!jobsContentEl) return;

    if (!jobsHtml || jobsHtml.trim() === '') {
      jobsContentEl.innerHTML = `
        <div class="container">
          <p class="jobs__empty">${this.t(
            'website.jobs.empty',
            'No jobs available right now.'
          )}</p>
        </div>
      `;
      return;
    }

    jobsContentEl.innerHTML = jobsHtml;
  }

  updateFilterOptions(filterOptions) {
    if (!filterOptions) return;

    this.updateSelect('location', filterOptions.locations || []);
    // this.updateSelect('activity_area', filterOptions.activity_areas || []);
    // this.updateSelect('career_level', filterOptions.career_levels || []);
    this.updateSelect('contract_type', filterOptions.contract_types || []);
    this.updateSelect('schedule_type', filterOptions.schedule_types || []);
  }

  updateSelect(fieldName, options) {
    const form = this.hasFormTarget ? this.formTarget : this.element;
    const select = form.querySelector(`select[name="${fieldName}"]`);
    if (!select) return;

    // Get current value
    const currentValue = select.value;

    // Clear existing options except "Alle"
    const allOption = select.querySelector('option[value=""]');
    select.innerHTML = '';
    if (allOption) {
      select.appendChild(allOption);
    }

    // Add new options
    options.forEach((option) => {
      const optionElement = document.createElement('option');
      optionElement.value = option;
      optionElement.textContent = option;
      select.appendChild(optionElement);
    });

    // Restore current value if it still exists
    if (
      currentValue &&
      Array.from(select.options).some((opt) => opt.value === currentValue)
    ) {
      select.value = currentValue;
    }
  }

  t(key, defaultValue) {
    // Use global I18n if available, otherwise fall back to provided default
    if (window.I18n && typeof window.I18n.t === 'function') {
      return window.I18n.t(key, { defaultValue });
    }
    return defaultValue;
  }
}
